// src/pages/bloomterminal/alerts/components/AlertCard.jsx
import React, { useState } from 'react';
import { ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import ToggleSwitch from './ToggleSwitch';

// Helper to format alert type for display
const formatAlertType = (alertType, specificAlertType) => {
  // Use specific alert type if available, otherwise use alert type
  const type = specificAlertType || alertType || '';
  // Convert snake_case to Title Case
  return type
    .split('_')
    .map((word) => {
      if (word.toLowerCase() === 'fob') return 'FOB';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

// Helper to capitalize commodity name
const formatCommodity = (commodity) => {
  if (!commodity) return '';
  return commodity.charAt(0).toUpperCase() + commodity.slice(1).toLowerCase();
};

const AlertCard = ({ 
  alert, 
  onToggle, 
  onDelete,
  onEdit,
  isToggling = false,
  isDeleting = false,
}) => {
  const { fontSize } = useGlobalFontSize();
  const { isDark } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isToggling) {
      onToggle(alert);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (!isDeleting) {
      onDelete(alert);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(alert);
  };

  const handleCardClick = () => {
    setIsExpanded(!isExpanded);
  };

  // Extract data from API response format
  const commodity = formatCommodity(alert.commodity);
  const alertType = formatAlertType(alert.alert_type, alert.specific_alert_type);
  const numberOfRegions = alert.number_of_regions || 0;

  return (
    <div 
      className={`bg-white dark:bg-[#282a29] border border-bloom-green rounded-[8px] transition-all duration-300 ease-in-out w-full overflow-hidden ${
        isExpanded ? 'max-h-[500px]' : 'max-h-[69px]'
      } ${isDeleting ? 'opacity-50' : ''}`}
      style={{
        transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <div className="rounded-[inherit]">
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 h-[69px] cursor-pointer flex-shrink-0 relative"
          onClick={handleCardClick}
        >
          <h3 
            className="font-['Inter'] font-semibold text-[#282A29] dark:text-white"
            style={{ fontSize: fontSize + 4 }}
          >
            {commodity}
          </h3>
          
          <div className="flex items-center gap-[16px]">
            {/* Chevron */}
            <div className="flex items-center justify-center shrink-0">
              <ChevronUp 
                size={24} 
                className={`text-[#282A29] dark:text-white transition-transform duration-300 ease-in-out ${
                  isExpanded ? 'rotate-0' : 'rotate-180'
                }`}
              />
            </div>
            
            {/* Toggle and Delete */}
            <div 
              className="flex items-center gap-[12px] shrink-0" 
              onClick={(e) => e.stopPropagation()}
            >
              <ToggleSwitch 
                enabled={alert.enabled} 
                onToggle={handleToggle} 
                disabled={isToggling}
              />
              <button
                onClick={handleEdit}
                className="flex items-center justify-center w-6 h-6 hover:opacity-70 transition-opacity"
                aria-label="Edit alert"
              >
                <Pencil size={24} className="text-[#5d625f]" />
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className={`flex items-center justify-center w-6 h-6 hover:opacity-70 transition-opacity ${
                  isDeleting ? 'cursor-not-allowed' : ''
                }`}
                aria-label="Delete alert"
              >
                <Trash2 size={24} className="text-[#5d625f]" />
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded 
              ? 'max-h-[200px] opacity-100' 
              : 'max-h-0 opacity-0'
          }`}
          style={{
            transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-in-out'
          }}
        >
          <div className="px-6 pb-6 pt-0">
            <div className="flex flex-col gap-3">
              {/* Commodity */}
              <div className="flex items-center justify-between h-[34px] border-b border-[#E8EBE9] dark:border-[#5d625f]">
                <span 
                  className="font-['Inter'] font-medium text-[#282A29] dark:text-white"
                  style={{ fontSize: fontSize }}
                >
                  Commodity:
                </span>
                <span 
                  className="font-['Inter'] font-normal text-[#282A29] dark:text-white"
                  style={{ fontSize: fontSize }}
                >
                  {commodity}
                </span>
              </div>

              {/* Alert Type */}
              <div className="flex items-center justify-between h-[34px] border-b border-[#E8EBE9] dark:border-[#5d625f]">
                <span 
                  className="font-['Inter'] font-medium text-[#282A29] dark:text-white"
                  style={{ fontSize: fontSize }}
                >
                  Alert type:
                </span>
                <span 
                  className="font-['Inter'] font-normal text-[#282A29] dark:text-white"
                  style={{ fontSize: fontSize }}
                >
                  {alertType}
                </span>
              </div>

              {/* Number of Regions */}
              <div className="flex items-center justify-between h-[25px]">
                <span 
                  className="font-['Inter'] font-medium text-[#282A29] dark:text-white"
                  style={{ fontSize: fontSize }}
                >
                  Number of regions:
                </span>
                <span 
                  className="font-['Inter'] font-normal text-[#282A29] dark:text-white"
                  style={{ fontSize: fontSize }}
                >
                  {numberOfRegions}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertCard;

